/**
 * Radar chart component for multi-dimensional analysis
 */

import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import BaseChart from './BaseChart';
import { ChartTheme, getChartColors } from '../../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface RadarChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string;
      borderColor?: string;
      borderWidth?: number;
      pointBackgroundColor?: string;
      pointBorderColor?: string;
      pointRadius?: number;
    }>;
  };
  theme: ChartTheme;
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  title?: string;
  showLegend?: boolean;
  maxValue?: number;
  stepSize?: number;
}

const RadarChart: React.FC<RadarChartProps> = ({
  data,
  theme,
  loading = false,
  error = null,
  height = 300,
  className = '',
  title,
  showLegend = true,
  maxValue,
  stepSize
}) => {
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: title ? {
        display: true,
        text: title,
        color: theme.text,
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 16,
          weight: 'bold' as const
        }
      } : undefined,
      legend: {
        display: showLegend,
        labels: {
          color: theme.text,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: theme.background,
        titleColor: theme.text,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13,
          weight: 'bold' as const
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: maxValue,
        stepSize: stepSize,
        grid: {
          color: theme.grid
        },
        angleLines: {
          color: theme.grid
        },
        pointLabels: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 10
          }
        }
      }
    },
    elements: {
      line: {
        borderWidth: 2,
        tension: 0.1
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2
      }
    }
  };

  // Process data to add default colors if not provided
  const processedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => {
      const colors = getChartColors(theme, data.datasets.length);
      return {
        ...dataset,
        backgroundColor: dataset.backgroundColor || `${colors[index]}20`,
        borderColor: dataset.borderColor || colors[index],
        pointBackgroundColor: dataset.pointBackgroundColor || colors[index],
        pointBorderColor: dataset.pointBorderColor || colors[index],
        borderWidth: dataset.borderWidth || 2,
        pointRadius: dataset.pointRadius || 4
      };
    })
  };

  return (
    <BaseChart
      data={processedData}
      options={options}
      theme={theme}
      loading={loading}
      error={error}
      height={height}
      className={className}
    >
      {({ data, options, height }) => (
        <Radar data={data as any} options={options as any} />
      )}
    </BaseChart>
  );
};

export default RadarChart;
